import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ShieldCheck, GraduationCap, KeyRound, ArrowRight, FileSignature } from 'lucide-react';
import { useApp } from '../contexts/AppContext';

/**
 * Garde réservant l'accès aux enseignants VALIDÉS uniquement.
 *
 * Différent de PremiumGate (qui était une vente).
 * Ici, l'enseignant doit obligatoirement passer par :
 *   1. Candidature en ligne (/rejoindre-enseignant)
 *   2. Validation manuelle par l'admin
 *   3. Réception d'un code d'accès personnel
 *
 * Aucun paiement n'est possible — c'est gratuit mais sous contrôle qualité.
 */
export function TeacherAccessGate({ title = "Espace réservé aux enseignants ECSR validés", description, testid = 'teacher-gate' }) {
  const navigate = useNavigate();
  const { user } = useApp();
  const isLogged = !!user;

  return (
    <div className="space-y-6" data-testid={testid}>
      <div className="mc-card p-6 sm:p-10 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full blur-3xl opacity-20" style={{ background: 'hsl(var(--primary))' }} />
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary-soft))] px-3 py-1 text-xs font-bold text-[hsl(var(--primary-dark))] mb-4">
            <ShieldCheck className="w-3.5 h-3.5" /> ACCÈS VALIDÉ ADMIN
          </div>
          <h1 className="text-2xl sm:text-4xl font-black tracking-tight leading-[1.05]">
            {title}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-3 max-w-2xl leading-relaxed">
            {description || "Cet espace contient les outils pédagogiques avancés (Guide ECSR, études de cas CCS, cours plateau et terrain). Pour garantir la qualité du contenu et la communauté, l'accès est réservé aux moniteurs ECSR dont la candidature a été validée par l'administrateur."}
          </p>

          <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#16a34a]/12 text-[#16a34a] text-xs font-black uppercase tracking-widest border border-[#16a34a]/25">
            <GraduationCap className="w-3.5 h-3.5" /> 100% gratuit pour les enseignants validés
          </div>
        </div>
      </div>

      {/* Parcours en 3 étapes */}
      <div className="mc-card p-5 sm:p-7" data-testid="teacher-gate-steps">
        <h2 className="text-lg sm:text-xl font-black mb-4">Comment obtenir l'accès ?</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            {
              n: 1,
              icon: FileSignature,
              title: 'Déposez votre candidature',
              desc: "Remplissez le formulaire en ligne (école, expérience, motivations). Compte 5 min.",
              color: '#1d4ed8',
              soft: '#eef5ff',
            },
            {
              n: 2,
              icon: ShieldCheck,
              title: 'Validation admin',
              desc: "L'administrateur examine votre profil et valide manuellement (qualité garantie).",
              color: '#f59e0b',
              soft: '#fff7ed',
            },
            {
              n: 3,
              icon: KeyRound,
              title: 'Code d\'accès envoyé',
              desc: 'Vous recevez un code personnel à utiliser à l\'inscription. Accès immédiat.',
              color: '#16a34a',
              soft: '#eaf8ef',
            },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.n} className="rounded-2xl border border-border p-4 relative">
                <span
                  className="absolute -top-3 left-4 inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-black text-white"
                  style={{ background: s.color }}
                >
                  {s.n}
                </span>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mt-2 mb-3" style={{ background: s.soft, color: s.color }}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="font-bold text-sm">{s.title}</div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* CTAs */}
      <div className="mc-card p-5 sm:p-7 flex flex-wrap gap-3 items-center justify-between" data-testid="teacher-gate-ctas">
        <div className="min-w-0">
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Action</div>
          <div className="text-base font-bold mt-0.5">Déjà candidat ou nouveau ? On vous guide.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => navigate('/rejoindre-enseignant')}
            className="mc-btn-primary"
            data-testid="teacher-gate-apply"
          >
            <FileSignature className="w-4 h-4 mr-1.5" /> Déposer ma candidature
          </Button>
          {!isLogged && (
            <Button
              onClick={() => navigate('/login')}
              variant="outline"
              data-testid="teacher-gate-login"
            >
              <KeyRound className="w-4 h-4 mr-1.5" /> J'ai déjà un code → Connexion
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          )}
        </div>
      </div>

      {/* Mention de soutien */}
      <p className="text-xs text-muted-foreground text-center px-4" data-testid="teacher-gate-mention">
        🤝 Code 2 Roues est <strong>gratuit pour les enseignants ECSR</strong>. La validation manuelle garantit la qualité et la cohérence pédagogique du réseau.
      </p>
    </div>
  );
}
